import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import {
  EmptyStatePill, EnterpriseScreenShell, InlineNotice, actionButtonFactory, type FormModalMode,
} from 'components/enterprise-shell';
import type { ActionButton } from 'components/modals';
import { SyutGrid, SyutSelect, type ActionMenuItem, type SelectOption, type SyutGridColumn } from 'components/ui';
import GridLoadingSkeleton from 'components/common/GridLoadingSkeleton';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import notificationService from 'services/notificationService';
import { chargeTaxMappingService } from 'api/services/chargeTaxMappingService';
import { taxMasterService } from 'api/services/taxMasterService';
import { configService } from 'api/services/configService';
import { propertyService } from 'api/services/propertyService';
import { useDefaultProperty } from 'hooks/api/useDefaultProperty';
import { useTaxPack } from 'hooks/api/useTaxPack';
import type { ChargeTaxMapping, ChargeType } from 'api/types/chargeTaxMapping.types';
import { useChargeTypeLookup } from 'hooks/api/useChargeTypes';
import ChargeTaxMappingFormModal, {
  appliesToAll, treatmentLabel, type ChargeTaxMappingForm,
} from './ChargeTaxMappingFormModal';
import { chargeSubject, findCoverageGaps, formatDay, gapNotice, todayIso } from './taxRules';
import './charge-tax-mapping.css';

/**
 * ============================================================================
 * CHARGE TAX MAPPINGS — the property's tax rules
 * ============================================================================
 * A tax rule says which tax master a Room, Meal Plan or Add-on charge (or one
 * item of it) is taxed under, from a date. Rules are append-only: "Replace"
 * adds a successor from a later date (the server ends the current rule), "Stop"
 * ends a current rule with no successor. Bookings for a property with tax
 * calculation on are refused when a charge has no rule on the check-in date;
 * the coverage notice in the context bar warns about any such gap.
 *
 * No guidance takes a row of its own: the notice sits in the context bar, the
 * outcome of an action is stated inside its form.
 * ============================================================================
 */

interface ChargeTaxMappingsScreenProps {
  tabId?: string;
  moduleIcon?: string;
  moduleName?: string;
  allowedActions?: string[];
}

interface EditorState {
  mode: FormModalMode;
  record: ChargeTaxMapping | null;
  preset?: Partial<ChargeTaxMappingForm>;
}

const PAGE_SIZE_DEFAULT = 25;
/** The API caps a page at 100 rows. */
const REFERENCE_PAGE_SIZE = 100;

const ChargeTaxMappingsScreen: React.FC<ChargeTaxMappingsScreenProps> = ({
  tabId, moduleName = 'Charge Tax Mappings', allowedActions = [],
}) => {
  const canAdd = allowedActions.includes('add');
  const canEdit = allowedActions.includes('edit');

  const { pack, loading: packLoading, error: packError, refetch: refetchPack } = useTaxPack();
  // Charge types come from the Charge Types master (PMS slice 2), fetched on every load.
  const { options: chargeTypeOptions, nameOf: chargeTypeLabel } = useChargeTypeLookup();
  const { defaultPropertyId, loading: defaultPropertyLoading } = useDefaultProperty();
  const { isMinimized, dismiss: dismissMinimized, getMinimizedModal } = useModalMinimize();

  // ---- context: property, charge type, history ----------------------------------------------
  const [propertyId, setPropertyId] = useState('');
  const [propertyLabel, setPropertyLabel] = useState('');
  const [chargeType, setChargeType] = useState<ChargeType | ''>('');
  const [includeHistory, setIncludeHistory] = useState(false);
  const propertySeeded = useRef(false);

  useEffect(() => {
    if (defaultPropertyLoading || propertySeeded.current) return;
    propertySeeded.current = true;
    if (defaultPropertyId) setPropertyId(defaultPropertyId);
  }, [defaultPropertyId, defaultPropertyLoading]);

  const propertyOptionsRef = useRef<SelectOption[]>([]);
  const loadPropertyOptions = useCallback(async (input: string): Promise<SelectOption[]> => {
    const res = await propertyService.getAll({ searchTerm: input || undefined, pageSize: input ? 50 : 500, includeInactive: false });
    const options = (res.data ?? []).map((p: any) => ({ value: p.id, label: `${p.propertyCode} - ${p.propertyName}` }));
    propertyOptionsRef.current = options;
    return options;
  }, []);

  useEffect(() => {
    const match = propertyOptionsRef.current.find((o) => o.value === propertyId);
    if (match) { setPropertyLabel(String(match.label)); return; }
    if (!propertyId) { setPropertyLabel(''); return; }
    let cancelled = false;
    propertyService.getById(propertyId)
      .then((res: any) => {
        const p = res?.data;
        if (!cancelled && p) setPropertyLabel(`${p.propertyCode} - ${p.propertyName}`);
      })
      .catch(() => { /* label stays empty; the id still scopes the list */ });
    return () => { cancelled = true; };
  }, [propertyId]);

  // ---- server-side list -----------------------------------------------------------------------
  const [rows, setRows] = useState<ChargeTaxMapping[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_DEFAULT);
  const [loading, setLoading] = useState(false);
  // False until the first page for the selected property has arrived — the grid area shows the
  // skeleton rather than a momentary "no rules" message.
  const [listReady, setListReady] = useState(false);

  const load = useCallback(async () => {
    if (!propertyId) { setRows([]); setTotalCount(0); return; }
    setLoading(true);
    try {
      const res = await chargeTaxMappingService.getAll({
        propertyId, chargeType: chargeType || undefined, includeHistory, page, pageSize,
      });
      setRows(res.data);
      setTotalCount(res.pagination.totalItems);
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || err?.message || 'Failed to load tax rules.');
    } finally {
      setLoading(false);
      setListReady(true);
    }
  }, [propertyId, chargeType, includeHistory, page, pageSize]);

  useEffect(() => { void load(); }, [load]);

  // Context changes start again from page 1.
  useEffect(() => { setPage(1); }, [propertyId, chargeType, includeHistory]);

  // ---- reference data for guidance ------------------------------------------------------------
  // Every rule of the property (history included) for the coverage notice and the outcome
  // wording; whether tax calculation is on for the property; tax master names.
  const [allRules, setAllRules] = useState<ChargeTaxMapping[]>([]);
  const [taxEnabled, setTaxEnabled] = useState(false);
  const [rateNames, setRateNames] = useState<Map<string, string>>(new Map());

  const loadAllRules = useCallback(async () => {
    if (!propertyId) { setAllRules([]); return; }
    try {
      const collected: ChargeTaxMapping[] = [];
      for (let p = 1; p <= 20; p += 1) {
        const res = await chargeTaxMappingService.getAll({ propertyId, includeHistory: true, page: p, pageSize: REFERENCE_PAGE_SIZE });
        collected.push(...res.data);
        if (!res.pagination.hasNextPage) break;
      }
      setAllRules(collected);
    } catch {
      setAllRules([]); // guidance only; the grid reports load errors
    }
  }, [propertyId]);

  useEffect(() => { void loadAllRules(); }, [loadAllRules]);

  useEffect(() => {
    if (!propertyId) { setTaxEnabled(false); return; }
    let cancelled = false;
    configService.getEffectiveValue('tax_calculation_enabled', [{ type: 'PROPERTY', id: propertyId }])
      .then((res) => { if (!cancelled) setTaxEnabled(String(res?.data?.effectiveValue).toLowerCase() === 'true'); })
      .catch(() => { if (!cancelled) setTaxEnabled(false); });
    return () => { cancelled = true; };
  }, [propertyId]);

  useEffect(() => {
    if (!pack) return;
    let cancelled = false;
    (async () => {
      const names = new Map<string, string>();
      for (let p = 1; p <= 20; p += 1) {
        const res = await taxMasterService.list(pack.jurisdictionCode, { page: p, pageSize: REFERENCE_PAGE_SIZE });
        res.items.forEach((t) => names.set(t.rateCode, t.name));
        if (!res.hasNext) break;
      }
      if (!cancelled) setRateNames(names);
    })().catch(() => { /* codes are shown without names */ });
    return () => { cancelled = true; };
  }, [pack]);

  const rateLabel = useCallback((code: string) => rateNames.get(code) ?? code, [rateNames]);

  const today = todayIso();
  const notice = useMemo(
    () => (taxEnabled ? gapNotice(findCoverageGaps(allRules, today), taxEnabled, today) : null),
    [allRules, taxEnabled, today],
  );

  // ---- editor ---------------------------------------------------------------------------------
  const [editor, setEditor] = useState<EditorState | null>(null);
  const [createInstanceId, setCreateInstanceId] = useState<string>('1');

  const restoreIfMinimized = useCallback((modalId: string | null): boolean => {
    if (!modalId || !isMinimized(modalId)) return false;
    const modal = getMinimizedModal(modalId);
    dismissMinimized(modalId);
    modal?.onRestore?.();
    return true;
  }, [isMinimized, getMinimizedModal, dismissMinimized]);

  const openCreate = useCallback((preset?: Partial<ChargeTaxMappingForm>) => {
    if (!propertyId) {
      notificationService.toast.info('Select a property first.');
      return;
    }
    let next = 1;
    while (tabId && isMinimized(`${tabId}-form-modal-create-${next}`)) next += 1;
    setCreateInstanceId(String(next));
    setEditor({ mode: 'create', record: null, preset });
  }, [propertyId, tabId, isMinimized]);

  const handleReplace = useCallback((record: ChargeTaxMapping) => {
    if (restoreIfMinimized(tabId ? `${tabId}-form-modal-copy-${record.id}` : null)) return;
    setEditor({ mode: 'copy', record });
  }, [tabId, restoreIfMinimized]);

  const handleStop = useCallback((record: ChargeTaxMapping) => {
    if (restoreIfMinimized(tabId ? `${tabId}-form-modal-edit-${record.id}` : null)) return;
    setEditor({ mode: 'edit', record });
  }, [tabId, restoreIfMinimized]);

  const handleRestoreModal = useCallback(async (mode: FormModalMode, instanceId?: string, recordCode?: string) => {
    if (instanceId) setCreateInstanceId(instanceId);
    if (mode === 'create' || !recordCode) {
      setEditor({ mode, record: null });
      return;
    }
    // Replace / Stop act on one rule; it may have left the current page since it was minimized.
    let record = rows.find((r) => r.id === recordCode) ?? null;
    if (!record) {
      try {
        record = (await chargeTaxMappingService.getById(recordCode)).data ?? null;
      } catch {
        record = null;
      }
    }
    if (!record) {
      notificationService.toast.error('The tax rule could not be loaded. Refresh and try again.');
      return;
    }
    setEditor({ mode, record });
  }, [rows]);

  const refreshAll = useCallback(() => {
    void load();
    void loadAllRules();
  }, [load, loadAllRules]);

  const handleSaved = useCallback(() => {
    setEditor(null);
    refreshAll();
  }, [refreshAll]);

  // ---- grid -----------------------------------------------------------------------------------
  const columns = useMemo<SyutGridColumn<ChargeTaxMapping>[]>(() => [
    { id: 'chargeType', header: 'Charge', accessorKey: 'chargeType', size: 110, cell: ({ row }) => chargeTypeLabel(row.original.chargeType) },
    {
      id: 'item', header: 'Applies to', accessorKey: 'itemName', size: 200,
      cell: ({ row }) => row.original.itemName ?? <span className="ctm-dim">{appliesToAll(row.original.chargeType, chargeTypeLabel(row.original.chargeType))}</span>,
    },
    {
      id: 'rateCode', header: 'Tax master', accessorKey: 'rateCode', size: 200,
      // One line, like every other cell: a long master name is cut with "…" and shown in full on hover.
      cell: ({ row }) => {
        const name = rateNames.get(row.original.rateCode);
        return (
          <span className="ctm-one-line" title={name ? `${row.original.rateCode} · ${name}` : row.original.rateCode}>
            <span className="ctm-mono">{row.original.rateCode}</span>
            {name && <span className="ctm-dim-plain"> · {name}</span>}
          </span>
        );
      },
    },
    { id: 'sacHsnCode', header: 'SAC / HSN', accessorKey: 'sacHsnCode', size: 100, cell: ({ row }) => row.original.sacHsnCode || '—' },
    { id: 'taxTreatment', header: 'Tax status', accessorKey: 'taxTreatment', size: 110, cell: ({ row }) => treatmentLabel(row.original.taxTreatment) },
    { id: 'effectiveFrom', header: 'Valid from', accessorKey: 'effectiveFrom', size: 120, cell: ({ row }) => formatDay(row.original.effectiveFrom) },
    {
      id: 'effectiveTo', header: 'Valid until', accessorKey: 'effectiveTo', size: 150,
      cell: ({ row }) => {
        const to = row.original.effectiveTo;
        if (!to) return <span className="badge badge-active">Current</span>;
        // effectiveTo is exclusive: the rule's last day is the day before.
        if (to.slice(0, 10) > today) return <span className="badge badge-pending" title={`Last check-in covered: the day before ${formatDay(to)}`}>Until {formatDay(to)}</span>;
        return <span className="ctm-dim-plain">Ended {formatDay(to)}</span>;
      },
    },
    { id: 'remarks', header: 'Remarks', accessorKey: 'remarks', size: 220, cell: ({ row }) => row.original.remarks || '—' },
  ], [rateNames, today, chargeTypeLabel]);

  const getRowActions = useCallback((record: ChargeTaxMapping): ActionMenuItem[] => {
    const items: ActionMenuItem[] = [];
    // Only a current rule (no end date) can be replaced or stopped; the server enforces the same.
    if (record.effectiveTo) return items;
    const subject = chargeSubject(record.chargeType, record.itemName);
    const rate = rateLabel(record.rateCode);
    if (canAdd) {
      items.push({
        id: 'replace',
        label: 'Replace with a new rate from a date…',
        description: `${subject} use a different tax master from the date you choose. Earlier check-ins keep ${rate}.`,
        icon: <IconSystem.copy size={18} />,
        onClick: () => handleReplace(record),
      });
    }
    if (canEdit) {
      items.push({
        id: 'stop',
        label: 'Stop this rule from a date…',
        description: record.itemId
          ? `No replacement. From that date ${record.itemName ?? 'this item'} falls back to the general ${chargeTypeLabel(record.chargeType)} rule, if there is one.`
          : `No replacement. From that date ${subject.toLowerCase()} need another rule, or bookings with them are refused.`,
        icon: <IconSystem.calendar size={18} />,
        onClick: () => handleStop(record),
      });
    }
    return items;
  }, [canAdd, canEdit, handleReplace, handleStop, rateLabel, chargeTypeLabel]);

  const actionButtons: ActionButton[] = [
    { ...actionButtonFactory.add(() => openCreate(), 'Add tax rule'), visible: canAdd && Boolean(pack) },
    actionButtonFactory.refresh(refreshAll),
  ];

  // ---- render ---------------------------------------------------------------------------------
  // The header and context bar render from the first frame; only the grid area shows the
  // skeleton while the tax pack, the default property or the first page is still loading.
  const gridPending = packLoading || defaultPropertyLoading || (Boolean(propertyId) && !listReady);

  let body: React.ReactNode;
  if (gridPending) {
    body = <div className="data-grid-container"><GridLoadingSkeleton rows={10} columns={6} /></div>;
  } else if (packError || !pack) {
    body = (
      <EmptyStatePill
        variant="error"
        title="Tax configuration unavailable"
        message={packError ?? 'The tax pack could not be loaded for this tenant.'}
        onRefresh={() => { void refetchPack(); }}
      />
    );
  } else if (!propertyId) {
    body = <EmptyStatePill variant="empty" title="Select a property" message="Tax rules are kept per property." />;
  } else {
    body = (
      <div className="data-grid-container">
        <SyutGrid
          data={rows}
          columns={columns}
          enablePagination
          manualPagination
          totalCount={totalCount}
          currentPage={page - 1}
          pageSize={pageSize}
          onPageChange={(pageIndex) => setPage(pageIndex + 1)}
          onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
          isLoading={loading}
          rowActions={getRowActions}
          emptyMessage={includeHistory ? 'No tax rules for this property' : 'No tax rules in use for this property'}
        />
      </div>
    );
  }

  return (
    <EnterpriseScreenShell
      title="Charge Tax Mappings"
      description={`Tax rules: which tax master each charge type (room, meal plan, add-on, restaurant…) uses, from a date${pack ? ` (${pack.jurisdictionCode})` : ''}`}
      icon={<IconSystem.percent size={18} />}
      tabId={tabId}
      actionButtons={actionButtons}
    >
      <div className="ctm-context-bar">
        <div className="ctm-field ctm-field--property">
          <SyutSelect
            label={<>Property <span className="text-danger">*</span></>}
            isAsync
            loadOptions={loadPropertyOptions}
            defaultOptions
            cacheOptions={false}
            seedOptions={propertyId && propertyLabel ? [{ value: propertyId, label: propertyLabel }] : undefined}
            value={propertyId}
            onChange={(v) => setPropertyId((v as string) || '')}
            placeholder="Select property..."
            size="sm"
          />
        </div>
        <div className="ctm-field">
          <SyutSelect
            label="Charge"
            options={chargeTypeOptions}
            value={chargeType}
            onChange={(v) => setChargeType(((v as string) || '') as ChargeType | '')}
            placeholder="All charges"
            isClearable
            size="sm"
          />
        </div>
        <label className="ctm-history-toggle">
          <input type="checkbox" checked={includeHistory} onChange={(e) => setIncludeHistory(e.target.checked)} />
          Show rules no longer in use
        </label>
        {notice && (
          <div className="ctm-notice-slot">
            <InlineNotice
              tone={notice.tone}
              text={notice.text}
              detail={notice.detail}
              action={canAdd && pack ? { label: 'Add rule', onClick: () => openCreate({ chargeType: notice.chargeType }) } : undefined}
            />
          </div>
        )}
      </div>

      {body}

      {editor && pack && (
        <ChargeTaxMappingFormModal
          isOpen
          mode={editor.mode}
          record={editor.record}
          preset={editor.preset}
          propertyId={editor.record?.propertyId ?? propertyId}
          propertyLabel={propertyLabel}
          jurisdictionCode={editor.record?.jurisdictionCode ?? pack.jurisdictionCode}
          rules={allRules}
          rateLabel={rateLabel}
          chargeTypeOptions={chargeTypeOptions}
          chargeTypeName={chargeTypeLabel}
          taxEnabled={taxEnabled}
          tabId={tabId}
          moduleName={moduleName}
          createInstanceId={createInstanceId}
          onClose={() => setEditor(null)}
          onSaved={handleSaved}
          onRestoreModal={handleRestoreModal}
        />
      )}
    </EnterpriseScreenShell>
  );
};

export default ChargeTaxMappingsScreen;
